import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import CategoryIcon from "../icons/CategoryIcon";
import CategoryPosIcon from "../icons/CategoryPosIcon";
import StarIcon from "../icons/StarIcon";
import {
	buildFavoriteCategory,
	FAVORITES_CATEGORY_ID,
	getFirstAvailableCategory,
	loadFavoriteProductIds,
	PRODUCT_FAVORITES_EVENT,
} from "../utils/productFavorites";

function Sidebar() {
	const {
		apiSettings,
		posSettings,
		filteredCategories,
		category,
		setCategory,
		categoryPos,
		posCategories,
		mode,
		setMode,
		setCategoryPos,
		lightMode,
		user,
	} = useContext(AppContext);
	const [menuOpen, setMenuOpen] = useState(false);
	const [favoriteProductIds, setFavoriteProductIds] = useState([]);
	const isEnabled = (value) => value === 1 || value === "1" || value === true;
	const isSmartCashEnabled = [
		posSettings?.smart_cash,
		posSettings?.smart_pos,
		posSettings?.smart_cassa,
		posSettings?.cassa_smart,
		posSettings?.enable_smart_cash,
		apiSettings?.pos?.smart_cash,
		apiSettings?.pos?.smart_pos,
		apiSettings?.pos?.enable_smart_cash,
		apiSettings?.menu_api?.smart_cash,
		apiSettings?.menu_api?.smart_pos,
		apiSettings?.menu_api?.enable_smart_cash,
		apiSettings?.plugins?.smart_cash?.active,
		apiSettings?.plugins?.smart_pos?.active,
	].some(isEnabled);
	const isPosLightEnabled = isEnabled(apiSettings?.mandant?.pos_light) || isEnabled(lightMode);
	const hidePosAdvancedControls = isSmartCashEnabled || isPosLightEnabled;
	const hasPosCategories = Array.isArray(posCategories) && posCategories.length > 0;
	const showPosModeButton = !hidePosAdvancedControls && hasPosCategories;
	const hasMacroCategories = (filteredCategories || []).some(
		(course) => String(course?.name || "").trim().length > 0,
	);
	const favoritesCategory = buildFavoriteCategory(filteredCategories, favoriteProductIds);
	const hasFavoritesCategory = Boolean(favoritesCategory);
	const posCategoriesWithFavorites = hasFavoritesCategory
		? [favoritesCategory, ...(posCategories || [])]
		: posCategories || [];

	useEffect(() => {
		if ((!showPosModeButton || hidePosAdvancedControls) && Number(mode) === 2) setMode(1);
	}, [showPosModeButton, hidePosAdvancedControls, mode, setMode]);

	useEffect(() => {
		setFavoriteProductIds(loadFavoriteProductIds(user?.id));
	}, [user?.id]);

	useEffect(() => {
		function syncFavorites(event) {
			if (event?.type === "storage" && event.key && !event.key.includes("pos-product-favorites-"))
				return;
			setFavoriteProductIds(loadFavoriteProductIds(user?.id));
		}

		window.addEventListener("storage", syncFavorites);
		window.addEventListener(PRODUCT_FAVORITES_EVENT, syncFavorites);
		return () => {
			window.removeEventListener("storage", syncFavorites);
			window.removeEventListener(PRODUCT_FAVORITES_EVENT, syncFavorites);
		};
	}, [user?.id]);

	useEffect(() => {
		if (category?.id !== FAVORITES_CATEGORY_ID || hasFavoritesCategory) return;
		const fallbackCategory = getFirstAvailableCategory(filteredCategories);
		if (fallbackCategory) setCategory(fallbackCategory);
	}, [category, filteredCategories, hasFavoritesCategory, setCategory]);

	useEffect(() => {
		if (categoryPos?.id !== FAVORITES_CATEGORY_ID || hasFavoritesCategory) return;
		if (Array.isArray(posCategories) && posCategories.length > 0) setCategoryPos(posCategories[0]);
	}, [categoryPos, hasFavoritesCategory, posCategories, setCategoryPos]);

	function toggleSubmenu() {
		if (menuOpen) setMenuOpen(false);
		else setMenuOpen(true);
	}

	return (
		<div className="sidebar">
			<div className={"modes-buttons" + (!showPosModeButton ? " single" : "")}>
				<button
					onClick={() => {
						if (mode == 1) toggleSubmenu();
						else setMenuOpen(true);
						setMode(1);
					}}
					className={mode == 1 ? "selected" : ""}
				>
					<div className="icon-category">
						<CategoryIcon />
					</div>
					<div className="name-category">Categorie</div>
				</button>
				{showPosModeButton && (
					<button
						onClick={() => {
							if (mode == 2) toggleSubmenu();
							else setMenuOpen(true);
							setMode(2);
						}}
						className={mode == 2 ? "selected" : ""}
					>
						<div className="icon-category">
							<CategoryPosIcon />
						</div>
						<div className="name-category">Categorie POS</div>
					</button>
				)}
			</div>
			<div className={"categories-container scroll mode-" + mode + (menuOpen ? " open" : "")}>
				{mode == 1 &&
					filteredCategories && (
						<>
							{favoritesCategory && (
								<div className="buttons-group favorites-group">
									<button
										className={
											"button button-1" +
											(favoritesCategory.id == category.id ? " selected" : "") +
											" favorites-button"
										}
										onClick={() => {
											setCategory(favoritesCategory);
											setMenuOpen(false);
										}}
									>
										<span className="button-icon" aria-hidden="true">
											<StarIcon />
										</span>
										<span>Preferiti</span>
									</button>
								</div>
							)}
							{filteredCategories.map((course, i) => (
								<div key={i}>
									{(String(course.name || "").trim() || hasMacroCategories) && (
										<div className="course-name">
											{String(course.name || "").trim() || "Altro"}
										</div>
									)}
									{course.categories.map((cat, n) => (
										<div key={n} className="buttons-group">
											<button
												className={
													"button button-1" +
													(cat.id == category.id ? " selected" : "")
												}
												onClick={() => {
													setCategory(cat);
													setMenuOpen(false);
												}}
											>
												{cat.name}
											</button>
										</div>
									))}
								</div>
							))}
						</>
					)}
				{showPosModeButton &&
					mode == 2 &&
					posCategoriesWithFavorites.map((category, i) => {
						const isFavorites = category.id === FAVORITES_CATEGORY_ID;
						return (
							<div
								key={i}
								className={"buttons-group" + (isFavorites ? " favorites-group" : "")}
							>
								<button
									className={
										"button button-1" +
										(category.id == categoryPos.id ? " selected" : "") +
										(isFavorites ? " favorites-button" : "")
									}
									onClick={() => {
										setCategoryPos(category);
										setMenuOpen(false);
									}}
								>
									{isFavorites && (
										<span className="button-icon" aria-hidden="true">
											<StarIcon />
										</span>
									)}
									<span>{category.name}</span>
								</button>
							</div>
						);
					})}
			</div>
		</div>
	);
}

export default Sidebar;
